import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/hkApi";
import { toast } from "sonner";
import { BedDouble, ClipboardList, Layers, Minus, Plus, QrCode, RefreshCw, Refrigerator, Trash2, MessageCircle, Send, Loader2, ChevronUp, ChevronDown, Camera, Pencil } from "lucide-react";

const RoomEditor = ({ room, onChange }) => {
  const [extraLabel, setExtraLabel] = useState("");

  const addExtra = async () => {
    if (!extraLabel.trim()) return;
    try {
      await api.post(`/rooms/${room.id}/extra-tasks`, { label: extraLabel.trim() });
      setExtraLabel("");
      onChange();
    } catch { toast.error("Errore salvataggio lavoro extra"); }
  };

  const delRoom = async () => {
    try {
      await api.delete(`/rooms/${room.id}`);
      toast.success("Camera eliminata");
      onChange();
    } catch { toast.error("Errore eliminazione camera"); }
  };

  const delExtra = async (taskId) => {
    try {
      await api.delete(`/rooms/${room.id}/extra-tasks/${taskId}`);
      onChange();
    } catch { toast.error("Errore eliminazione"); }
  };

  return (
    <div className="bg-white border border-[#E8DCC8] rounded-2xl shadow-sm p-4 sm:p-5" data-testid={`settings-room-${room.id}`}>
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-[#4A1C17] text-[#F7EBDF] flex items-center justify-center shrink-0">
          <BedDouble className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-lg font-bold text-[#2B1410]">{room.number ? `${room.number}. ` : ""}{room.name}</h3>
        </div>
        <button data-testid={`delete-room-${room.id}`} onClick={delRoom} className="h-9 w-9 rounded-full border border-[#E8DCC8] flex items-center justify-center text-red-700 hover:bg-red-50 transition-colors shrink-0">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-4">
        <p className="text-xs font-semibold text-[#7C5A4E] uppercase tracking-wide">Lavori extra della camera</p>
        <div className="mt-2 space-y-2">
          {(room.extra_tasks || []).map((t) => (
            <div key={t.id} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#FAF3EE] border border-[#E8DCC8]" data-testid={`extra-task-${t.id}`}>
              <span className="text-sm flex-1 text-[#2B1410]">{t.label}</span>
              <button data-testid={`delete-extra-${t.id}`} onClick={() => delExtra(t.id)} className="text-red-700 hover:bg-red-50 rounded-full p-1 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          <div className="flex gap-2">
            <input
              data-testid={`extra-input-${room.id}`}
              value={extraLabel}
              onChange={(e) => setExtraLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addExtra()}
              placeholder="Es. Pulizia terrazza, camino…"
              className="flex-1 rounded-full border border-[#E8DCC8] bg-white px-4 py-2 text-sm text-[#2B1410] outline-none focus:border-[#4A1C17] transition-colors"
            />
            <button data-testid={`add-extra-${room.id}`} onClick={addExtra} className="rounded-full bg-[#4A1C17] hover:bg-[#2B1410] text-[#F7EBDF] px-4 py-2 text-sm font-semibold flex items-center gap-1 transition-colors">
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function WhatsappCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setS(data);
    } catch { toast.error("Errore caricamento notifiche"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings", s);
      setS(data);
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setBusy(false); }
  };
  const test = async () => {
    setTesting(true);
    try {
      await api.put("/settings", s);
      const { data } = await api.post("/notify-now");
      toast.success("Messaggio inviato su WhatsApp");
      console.log("preview:\n" + data.message_preview);
    } catch (e) { toast.error(e.response?.data?.detail || "Invio fallito"); }
    finally { setTesting(false); }
  };
  if (!s) return null;
  return (
    <div className="anim-in bg-white border border-[#E8DCC8] rounded-2xl shadow-md p-4 sm:p-5" data-testid="hk-notify-card">
      <h2 className="font-display text-xl font-bold text-[#2B1410] flex items-center gap-2">
        <MessageCircle className="h-5 w-5 text-emerald-600" /> Notifica WhatsApp governante
      </h2>
      <p className="text-sm text-[#7C5A4E] mt-1">
        Ogni mattina il sistema manda alla governante un riassunto della giornata (partenze, arrivi, in casa) via WhatsApp usando CallMeBot.
      </p>
      <details className="mt-2 text-xs text-[#7C5A4E]">
        <summary className="cursor-pointer font-semibold">Come ottenere l'API key CallMeBot (5 minuti)</summary>
        <ol className="list-decimal ml-5 mt-2 space-y-1">
          <li>La governante salva in rubrica il numero <strong>+34 644 66 32 26</strong></li>
          <li>Da quel numero WhatsApp le invia il messaggio: <em>"I allow callmebot to send me messages"</em></li>
          <li>Riceve dopo qualche minuto un messaggio con la sua <strong>API key</strong> personale</li>
          <li>Incolla la key qui sotto e clicca "Salva"</li>
        </ol>
      </details>

      <div className="mt-3 space-y-2">
        <div className="flex items-center gap-2">
          <input
            data-testid="hk-notify-toggle"
            type="checkbox"
            checked={s.notification_enabled}
            onChange={(e) => setS({ ...s, notification_enabled: e.target.checked })}
            className="h-4 w-4 accent-emerald-600"
          />
          <label className="text-sm text-[#2B1410] font-semibold">Invio automatico ogni mattina</label>
        </div>
        <input
          data-testid="hk-notify-name"
          value={s.governante_name}
          onChange={(e) => setS({ ...s, governante_name: e.target.value })}
          placeholder="Nome della governante (usato nel saluto)"
          className="w-full rounded-full border border-[#E8DCC8] bg-white px-4 py-2 text-sm text-[#2B1410] outline-none focus:border-[#4A1C17]"
        />
        <input
          data-testid="hk-notify-phone"
          value={s.governante_phone}
          onChange={(e) => setS({ ...s, governante_phone: e.target.value })}
          placeholder="Numero WhatsApp (es. +39 340 1234567)"
          className="w-full rounded-full border border-[#E8DCC8] bg-white px-4 py-2 text-sm text-[#2B1410] outline-none focus:border-[#4A1C17]"
        />
        <input
          data-testid="hk-notify-apikey"
          value={s.callmebot_apikey}
          onChange={(e) => setS({ ...s, callmebot_apikey: e.target.value })}
          placeholder="API key CallMeBot"
          className="w-full rounded-full border border-[#E8DCC8] bg-white px-4 py-2 text-sm text-[#2B1410] outline-none focus:border-[#4A1C17]"
        />
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-sm text-[#7C5A4E]">Orario invio:</label>
          <input
            data-testid="hk-notify-time"
            type="time"
            value={s.notification_time}
            onChange={(e) => setS({ ...s, notification_time: e.target.value })}
            className="rounded-full border border-[#E8DCC8] bg-white px-3 py-2 text-sm text-[#2B1410] outline-none focus:border-[#4A1C17]"
          />
          <button
            data-testid="hk-notify-save"
            onClick={save}
            disabled={busy}
            className="ml-auto rounded-full bg-[#4A1C17] hover:bg-[#2B1410] text-[#F7EBDF] px-5 py-2 text-sm font-semibold transition-colors disabled:opacity-50"
          >
            {busy ? "Salvo…" : "Salva"}
          </button>
          <button
            data-testid="hk-notify-test"
            onClick={test}
            disabled={testing || !s.governante_phone || !s.callmebot_apikey}
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-40"
          >
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invia test
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const [rooms, setRooms] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [fbItems, setFbItems] = useState([]);
  const [linen, setLinen] = useState([]);
  const [linenName, setLinenName] = useState("");
  const [roomName, setRoomName] = useState("");
  const [taskLabel, setTaskLabel] = useState("");
  const [fbName, setFbName] = useState("");
  const [fbPrice, setFbPrice] = useState("");
  const [fbImage, setFbImage] = useState("");

  const load = useCallback(async () => {
    try {
      const [r, t, f, l] = await Promise.all([api.get("/rooms"), api.get("/standard-tasks"), api.get("/frigobar-items"), api.get("/linen-items")]);
      setRooms(r.data);
      setTasks(t.data);
      setFbItems(f.data);
      setLinen(l.data);
    } catch { toast.error("Errore caricamento impostazioni"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addRoom = async () => {
    if (!roomName.trim()) return;
    try {
      await api.post("/rooms", { name: roomName.trim() });
      setRoomName("");
      toast.success("Camera aggiunta");
      load();
    } catch { toast.error("Errore aggiunta camera"); }
  };

  const addTask = async () => {
    if (!taskLabel.trim()) return;
    try {
      await api.post("/standard-tasks", { label: taskLabel.trim() });
      setTaskLabel("");
      load();
    } catch { toast.error("Errore aggiunta lavoro"); }
  };

  const delTask = async (id) => {
    try {
      await api.delete(`/standard-tasks/${id}`);
      load();
    } catch { toast.error("Errore eliminazione"); }
  };

  const addFb = async () => {
    if (!fbName.trim()) return;
    try {
      const priceStr = fbPrice ? `${parseFloat(String(fbPrice).replace(",", ".")).toFixed(2).replace(".", ",")} €` : "";
      await api.post("/frigobar-items", {
        name: fbName.trim(),
        price: priceStr,
        image_url: fbImage || "",
      });
      setFbName(""); setFbPrice(""); setFbImage("");
      load();
    } catch { toast.error("Errore aggiunta prodotto"); }
  };

  // Upload photo directly from Settings (works with the admin base64 endpoint;
  // the housekeeping page needs an admin token in localStorage — same one the
  // owner uses for /admin. If a housekeeper without admin token opens this,
  // the upload button is hidden further down.)
  const onPickFbImage = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const adminToken = localStorage.getItem("admin_token") || "";
      const res = await fetch(`${API_URL}/api/upload`, {
        method: "POST",
        headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {},
        body: form,
      });
      if (!res.ok) throw new Error("upload failed");
      const data = await res.json();
      setFbImage(data.url);
      toast.success("Immagine caricata");
    } catch {
      toast.error("Serve il token admin per caricare foto — apri prima /admin");
    }
  };

  // Update an existing item (price OR image) with the PATCH endpoint.
  const patchFb = async (item, patch) => {
    setFbItems((prev) => prev.map((i) => i.id === item.id ? { ...i, ...patch } : i));
    try { await api.patch(`/frigobar-items/${item.id}`, patch); }
    catch { toast.error("Errore aggiornamento"); load(); }
  };

  // Move an item up/down in the list via reorder endpoint.
  const moveFb = async (index, dir) => {
    const newIndex = index + dir;
    if (newIndex < 0 || newIndex >= fbItems.length) return;
    const next = [...fbItems];
    [next[index], next[newIndex]] = [next[newIndex], next[index]];
    setFbItems(next);
    try { await api.post("/frigobar-items/reorder", { ids: next.map((i) => i.id) }); }
    catch { toast.error("Errore riordino"); load(); }
  };

  const delFb = async (id) => {
    try {
      await api.delete(`/frigobar-items/${id}`);
      load();
    } catch { toast.error("Errore eliminazione"); }
  };

  const setDot = async (item, delta) => {
    const next = Math.max(1, (item.dotazione ?? 1) + delta);
    setFbItems((prev) => prev.map((i) => i.id === item.id ? { ...i, dotazione: next } : i));
    try { await api.put(`/frigobar-items/${item.id}/dotazione`, { dotazione: next }); }
    catch { toast.error("Errore aggiornamento dotazione"); load(); }
  };

  const addLinen = async () => {
    if (!linenName.trim()) return;
    try {
      await api.post("/linen-items", { name: linenName.trim() });
      setLinenName("");
      load();
    } catch { toast.error("Errore aggiunta biancheria"); }
  };

  const delLinen = async (id) => {
    try {
      await api.delete(`/linen-items/${id}`);
      load();
    } catch { toast.error("Errore eliminazione"); }
  };

  const setLinenDot = async (item, delta) => {
    const next = Math.max(0, (item.dotazione ?? 0) + delta);
    setLinen((prev) => prev.map((i) => i.id === item.id ? { ...i, dotazione: next } : i));
    try { await api.put(`/linen-items/${item.id}/dotazione`, { dotazione: next }); }
    catch { toast.error("Errore aggiornamento dotazione"); load(); }
  };

  const setLinenStock = async (item, delta) => {
    const next = Math.max(0, (item.stock ?? 0) + delta);
    setLinen((prev) => prev.map((i) => i.id === item.id ? { ...i, stock: next } : i));
    try { await api.put(`/linen-items/${item.id}/stock`, { stock: next }); }
    catch { toast.error("Errore aggiornamento stock"); load(); }
  };
  const setLinenStockDirect = async (item, value) => {
    const next = Math.max(0, Math.floor(Number(value) || 0));
    setLinen((prev) => prev.map((i) => i.id === item.id ? { ...i, stock: next } : i));
    try { await api.put(`/linen-items/${item.id}/stock`, { stock: next }); }
    catch { toast.error("Errore aggiornamento stock"); load(); }
  };

  return (
    <div data-testid="settings-page" className="max-w-2xl mx-auto px-4 -mt-6 space-y-6">
      <WhatsappCard />
      <div className="anim-in bg-white border border-[#E8DCC8] rounded-2xl shadow-md p-4 sm:p-5">
        <h2 className="font-display text-xl font-bold text-[#2B1410] flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-[#4A1C17]" /> Lavori standard
        </h2>
        <p className="text-sm text-[#7C5A4E] mt-1">Questi lavori vengono proposti ogni giorno per tutte le camere occupate.</p>
        <div className="mt-3 space-y-2" data-testid="standard-tasks-list">
          {tasks.map((t) => (
            <div key={t.id} className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[#FAF3EE] border border-[#E8DCC8]" data-testid={`std-task-${t.id}`}>
              <span className="text-sm flex-1 text-[#2B1410]">{t.label}</span>
              <button data-testid={`delete-std-${t.id}`} onClick={() => delTask(t.id)} className="text-red-700 hover:bg-red-50 rounded-full p-1 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          <div className="flex gap-2">
            <input
              data-testid="std-task-input"
              value={taskLabel}
              onChange={(e) => setTaskLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addTask()}
              placeholder="Nuovo lavoro standard…"
              className="flex-1 rounded-full border border-[#E8DCC8] bg-white px-4 py-2 text-sm text-[#2B1410] outline-none focus:border-[#4A1C17] transition-colors"
            />
            <button data-testid="add-std-task-btn" onClick={addTask} className="rounded-full bg-[#4A1C17] hover:bg-[#2B1410] text-[#F7EBDF] px-4 py-2 text-sm font-semibold flex items-center gap-1 transition-colors">
              <Plus className="h-4 w-4" /> Aggiungi
            </button>
          </div>
        </div>
      </div>

      <div className="anim-in bg-white border border-[#E8DCC8] rounded-2xl shadow-md p-4 sm:p-5">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="font-display text-xl font-bold text-[#2B1410] flex items-center gap-2 flex-1">
            <Refrigerator className="h-5 w-5 text-[#4A1C17]" /> Frigo bar
          </h2>
          <button
            data-testid="fb-normalize-all"
            onClick={async () => {
              if (!window.confirm("Allineare tutte le camere alle dotazioni correnti? Le quantità 'presente' in ogni camera verranno reimpostate ai valori standard.")) return;
              try {
                const { data } = await api.post("/frigobar-items/normalize-all");
                toast.success(`Allineate ${data.rooms} camere su ${data.items} articoli`);
              } catch (e) { toast.error(e.response?.data?.detail || "Allineamento fallito"); }
            }}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#FAF3EE] hover:bg-[#4A1C17] hover:text-white border border-[#E8DCC8] text-[#4A1C17] px-3 py-1.5 text-xs font-semibold transition-colors"
            title="Riporta la quantità presente in ogni camera al valore di dotazione"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Allinea camere
          </button>
          <button
            data-testid="fb-sync-images"
            onClick={async () => {
              try {
                const { data } = await api.post("/frigobar/sync-images");
                await load();
                toast.success(`Immagini sincronizzate: ${data.matched} aggiornate su ${data.total}`);
              } catch (e) { toast.error(e.response?.data?.detail || "Sync fallito"); }
            }}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#FAF3EE] hover:bg-[#4A1C17] hover:text-white border border-[#E8DCC8] text-[#4A1C17] px-3 py-1.5 text-xs font-semibold transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Sincronizza immagini dal menu
          </button>
        </div>
        <p className="text-sm text-[#7C5A4E] mt-1">Prodotti presenti nel frigo bar di ogni camera. Il personale li segna come consumati o riforniti dalla checklist giornaliera. Le immagini vengono prese dal menu "Frigo Bar Alloggi" — clicca "Sincronizza" dopo aver aggiornato le foto lì.</p>
        <div className="mt-3 space-y-2" data-testid="frigobar-items-list">
          {fbItems.map((item, idx) => (
            <div key={item.id} className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[#FAF3EE] border border-[#E8DCC8]" data-testid={`fb-item-${item.id}`}>
              {/* Reorder arrows */}
              <div className="flex flex-col gap-0.5 shrink-0">
                <button
                  disabled={idx === 0}
                  onClick={() => moveFb(idx, -1)}
                  className="h-4 w-5 rounded border border-[#E8DCC8] bg-white flex items-center justify-center text-[#4A1C17] disabled:opacity-30"
                  data-testid={`fb-up-${item.id}`}
                  title="Sposta su"
                >
                  <ChevronUp className="h-3 w-3" />
                </button>
                <button
                  disabled={idx === fbItems.length - 1}
                  onClick={() => moveFb(idx, 1)}
                  className="h-4 w-5 rounded border border-[#E8DCC8] bg-white flex items-center justify-center text-[#4A1C17] disabled:opacity-30"
                  data-testid={`fb-down-${item.id}`}
                  title="Sposta giù"
                >
                  <ChevronDown className="h-3 w-3" />
                </button>
              </div>
              {/* Image with hover-replace on click */}
              <label className="relative cursor-pointer shrink-0" title="Cambia foto">
                {item.image_url ? (
                  <img src={item.image_url} alt={item.name} data-testid={`fb-img-${item.id}`} className="h-9 w-9 rounded-lg object-cover border border-[#E8DCC8] bg-white" />
                ) : (
                  <span className="h-9 w-9 rounded-lg border border-dashed border-[#c9b39a] bg-white flex items-center justify-center text-[#7C5A4E]">
                    <Camera className="h-4 w-4" />
                  </span>
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const form = new FormData();
                    form.append("file", file);
                    try {
                      const API_URL = process.env.REACT_APP_BACKEND_URL;
                      const adminToken = localStorage.getItem("admin_token") || "";
                      const res = await fetch(`${API_URL}/api/upload`, {
                        method: "POST",
                        headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {},
                        body: form,
                      });
                      if (!res.ok) throw new Error();
                      const data = await res.json();
                      await patchFb(item, { image_url: data.url });
                      toast.success("Foto aggiornata");
                    } catch { toast.error("Serve login admin per cambiare foto"); }
                    e.target.value = "";
                  }}
                  data-testid={`fb-img-input-${item.id}`}
                />
              </label>
              <span className="text-sm flex-1 text-[#2B1410]">{item.name}</span>
              {/* Editable price */}
              <input
                type="text"
                inputMode="decimal"
                value={item.price || ""}
                onChange={(e) => setFbItems((prev) => prev.map((i) => i.id === item.id ? { ...i, price: e.target.value } : i))}
                onBlur={(e) => {
                  // Normalize on blur: accept "5" / "5,00" / "5.00 €" / "5,00€" → "5,00 €"
                  const raw = (e.target.value || "").replace(/[^\d,.-]/g, "").replace(",", ".");
                  const n = parseFloat(raw);
                  const norm = Number.isFinite(n) && n > 0 ? `${n.toFixed(2).replace(".", ",")} €` : "";
                  if (norm !== item.price) patchFb(item, { price: norm });
                  else setFbItems((prev) => prev.map((i) => i.id === item.id ? { ...i, price: norm } : i));
                }}
                placeholder="€"
                className="w-16 text-xs text-right text-[#7C5A4E] bg-transparent border-b border-[#E8DCC8] focus:border-[#4A1C17] focus:outline-none px-1"
                data-testid={`fb-price-edit-${item.id}`}
              />
              <span className="text-xs text-[#7C5A4E] flex items-center gap-1">
                Dotazione:
                <button data-testid={`dot-minus-${item.id}`} onClick={() => setDot(item, -1)} className="h-5 w-5 rounded-full border border-[#E8DCC8] bg-white flex items-center justify-center text-[#4A1C17]"><Minus className="h-3 w-3" /></button>
                <span data-testid={`dot-value-${item.id}`} className="font-bold text-[#2B1410] w-4 text-center">{item.dotazione ?? 1}</span>
                <button data-testid={`dot-plus-${item.id}`} onClick={() => setDot(item, 1)} className="h-5 w-5 rounded-full border border-[#E8DCC8] bg-white flex items-center justify-center text-[#4A1C17]"><Plus className="h-3 w-3" /></button>
              </span>
              <button
                data-testid={`fb-normalize-${item.id}`}
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/frigobar-items/${item.id}/normalize-rooms`);
                    toast.success(`Allineate ${data.rooms} camere a ${data.dotazione}`);
                  } catch (e) { toast.error(e.response?.data?.detail || "Allineamento fallito"); }
                }}
                className="text-[#4A1C17] hover:bg-[#FAF3EE] rounded-full p-1 transition-colors"
                title="Allinea questa dotazione in tutte le camere"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
              <button data-testid={`delete-fb-${item.id}`} onClick={() => delFb(item.id)} className="text-red-700 hover:bg-red-50 rounded-full p-1 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {/* Preview / picker for the new item's photo */}
            <label className="relative cursor-pointer shrink-0" title="Aggiungi foto">
              {fbImage ? (
                <img src={fbImage} alt="" className="h-10 w-10 rounded-lg object-cover border border-[#E8DCC8] bg-white" />
              ) : (
                <span className="h-10 w-10 rounded-lg border border-dashed border-[#c9b39a] bg-white flex items-center justify-center text-[#7C5A4E]">
                  <Camera className="h-5 w-5" />
                </span>
              )}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onPickFbImage(e.target.files?.[0])}
                data-testid="fb-new-image-input"
              />
            </label>
            <input
              data-testid="fb-name-input"
              value={fbName}
              onChange={(e) => setFbName(e.target.value)}
              placeholder="Nuovo prodotto…"
              className="flex-1 min-w-[140px] rounded-full border border-[#E8DCC8] bg-white px-4 py-2 text-sm text-[#2B1410] outline-none focus:border-[#4A1C17] transition-colors"
            />
            <div className="relative">
              <input
                data-testid="fb-price-input"
                type="text"
                inputMode="decimal"
                value={fbPrice}
                onChange={(e) => setFbPrice(e.target.value.replace(/[^\d,.-]/g, ""))}
                placeholder="5,00"
                className="w-24 rounded-full border border-[#E8DCC8] bg-white pl-4 pr-7 py-2 text-sm text-[#2B1410] outline-none focus:border-[#4A1C17] transition-colors"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#7C5A4E] pointer-events-none">€</span>
            </div>
            <button data-testid="add-fb-btn" onClick={addFb} className="rounded-full bg-[#4A1C17] hover:bg-[#2B1410] text-[#F7EBDF] px-4 py-2 text-sm font-semibold flex items-center gap-1 transition-colors">
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="anim-in bg-white border border-[#E8DCC8] rounded-2xl shadow-md p-4 sm:p-5">
        <h2 className="font-display text-xl font-bold text-[#2B1410] flex items-center gap-2">
          <Layers className="h-5 w-5 text-[#4A1C17]" /> Biancheria
        </h2>
        <p className="text-sm text-[#7C5A4E] mt-1">Scorta pulita in <strong>magazzino/lavanderia</strong>. La governante consuma dallo stock quando "mette" un pezzo in camera; segna "tolto" quando lo riporta indietro.</p>
        <div className="mt-3 space-y-2" data-testid="linen-items-list">
          {linen.map((item) => (
            <div key={item.id} className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[#FAF3EE] border border-[#E8DCC8]" data-testid={`linen-item-${item.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#2B1410] truncate">{item.name}</div>
                {item.in_camere > 0 && (
                  <div className="text-[10px] text-[#7C5A4E]">In camere: {item.in_camere}</div>
                )}
              </div>
              <span className="text-xs text-[#7C5A4E] flex items-center gap-1 shrink-0">
                Magazzino:
                <button data-testid={`linen-stock-minus-${item.id}`} onClick={() => setLinenStock(item, -1)} className="h-6 w-6 rounded-full border border-[#E8DCC8] bg-white flex items-center justify-center text-[#4A1C17]"><Minus className="h-3 w-3" /></button>
                <input
                  data-testid={`linen-stock-value-${item.id}`}
                  type="number"
                  min={0}
                  value={item.stock ?? 0}
                  onChange={(e) => setLinenStockDirect(item, e.target.value)}
                  className="font-bold text-[#2B1410] w-12 text-center bg-white rounded-lg border border-[#E8DCC8] py-0.5 text-sm outline-none focus:border-[#4A1C17]"
                />
                <button data-testid={`linen-stock-plus-${item.id}`} onClick={() => setLinenStock(item, 1)} className="h-6 w-6 rounded-full border border-[#E8DCC8] bg-white flex items-center justify-center text-[#4A1C17]"><Plus className="h-3 w-3" /></button>
              </span>
              <button data-testid={`delete-linen-${item.id}`} onClick={() => delLinen(item.id)} className="text-red-700 hover:bg-red-50 rounded-full p-1 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          <div className="flex gap-2">
            <input
              data-testid="linen-name-input"
              value={linenName}
              onChange={(e) => setLinenName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addLinen()}
              placeholder="Nuovo tipo (es. Copriletto)…"
              className="flex-1 rounded-full border border-[#E8DCC8] bg-white px-4 py-2 text-sm text-[#2B1410] outline-none focus:border-[#4A1C17] transition-colors"
            />
            <button data-testid="add-linen-btn" onClick={addLinen} className="rounded-full bg-[#4A1C17] hover:bg-[#2B1410] text-[#F7EBDF] px-4 py-2 text-sm font-semibold flex items-center gap-1 transition-colors">
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="anim-in bg-white border border-[#E8DCC8] rounded-2xl shadow-md p-4 sm:p-5">
        <h2 className="font-display text-xl font-bold text-[#2B1410] flex items-center gap-2">
          <BedDouble className="h-5 w-5 text-[#4A1C17]" /> Camere
        </h2>
        <p className="text-sm text-[#7C5A4E] mt-1">
          Gestisci le camere. Arrivi e partenze si inseriscono nella pagina Calendario.
        </p>
        <div className="mt-3 space-y-2">
          <div className="flex gap-2">
            <input
              data-testid="room-name-input"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addRoom()}
              placeholder="Nome camera (es. Argo)"
              className="flex-1 rounded-full border border-[#E8DCC8] bg-white px-4 py-2 text-sm text-[#2B1410] outline-none focus:border-[#4A1C17] transition-colors"
            />
            <button data-testid="add-room-btn" onClick={addRoom} className="rounded-full bg-[#4A1C17] hover:bg-[#2B1410] text-[#F7EBDF] px-4 py-2 text-sm font-semibold flex items-center gap-1 transition-colors">
              <Plus className="h-4 w-4" /> Aggiungi
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {rooms.map((room) => <RoomEditor key={room.id} room={room} onChange={load} />)}
      </div>

      <div className="anim-in bg-white border border-[#E8DCC8] rounded-2xl shadow-md p-4 sm:p-5 text-center">
        <h2 className="font-display text-xl font-bold text-[#2B1410] flex items-center justify-center gap-2">
          <QrCode className="h-5 w-5 text-[#4A1C17]" /> QR per la governante
        </h2>
        <p className="text-sm text-[#7C5A4E] mt-1">Stampa questo QR: chi lo inquadra con il telefono apre direttamente il planner pulizie di oggi. Serve comunque la password admin al primo accesso.</p>
        <img
          src={`${process.env.REACT_APP_BACKEND_URL}/api/hk/qr?url=${encodeURIComponent(`${window.location.origin}/admin/pulizie/oggi`)}`}
          alt="QR app pulizie"
          data-testid="qr-code-img"
          className="mx-auto mt-4 h-56 w-56 rounded-xl border border-[#E8DCC8] bg-white p-2"
        />
        <p className="text-xs text-[#7C5A4E] mt-3 break-all">{window.location.origin}/admin/pulizie/oggi</p>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/hk/qr?url=${encodeURIComponent(`${window.location.origin}/admin/pulizie/oggi`)}`}
          download="qr-governante.png"
          data-testid="qr-download-btn"
          className="inline-flex items-center gap-1.5 mt-3 rounded-full bg-[#4A1C17] hover:bg-[#2B1410] text-[#F7EBDF] px-5 py-2 text-sm font-semibold transition-colors"
        >
          Scarica PNG
        </a>
      </div>
    </div>
  );
}
